import React from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { User, Save, Loader2, KeyRound, Eye, EyeOff } from "lucide-react";

export default function ProfilePanel() {
  const { user, checkAuth } = useAuth();
  const [name, setName] = React.useState(user?.name || "");
  const [phone, setPhone] = React.useState(user?.phone || "");
  const [savingProfile, setSavingProfile] = React.useState(false);

  // Password change
  const [curPw, setCurPw] = React.useState("");
  const [newPw, setNewPw] = React.useState("");
  const [confPw, setConfPw] = React.useState("");
  const [showPw, setShowPw] = React.useState(false);
  const [savingPw, setSavingPw] = React.useState(false);

  React.useEffect(() => {
    setName(user?.name || "");
    setPhone(user?.phone || "");
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/auth/profile", { name: name.trim(), phone: phone.trim() });
      toast.success("Profil berhasil diperbarui");
      await checkAuth();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Gagal memperbarui profil");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPw !== confPw) { toast.error("Konfirmasi password tidak cocok"); return; }
    if (newPw.length < 8) { toast.error("Password baru minimal 8 karakter"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      toast.success("Password berhasil diubah");
      setCurPw(""); setNewPw(""); setConfPw("");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Gagal mengubah password");
    } finally {
      setSavingPw(false);
    }
  };

  const hasPassword = user?.email && !user?.picture; // rough heuristic: Google logins usually have picture

  return (
    <div className="space-y-6" data-testid="profile-panel">
      {/* Profile info */}
      <div className="bg-card border border-border rounded-2xl p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-9 w-9 rounded-full bg-accent/15 flex items-center justify-center">
            <User className="h-4 w-4 text-accent" />
          </div>
          <div>
            <h2 className="font-serif text-xl text-primary">Data Profil</h2>
            <p className="text-xs text-muted-foreground">Edit nama dan nomor kontak Anda.</p>
          </div>
        </div>
        <form onSubmit={saveProfile} className="grid sm:grid-cols-2 gap-4" data-testid="profile-form">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Email</label>
            <input value={user?.email || ""} disabled className="mt-1.5 w-full px-3 py-2.5 border border-border rounded-md bg-muted text-sm cursor-not-allowed" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Nama Lengkap (sesuai paspor)</label>
            <input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} className="mt-1.5 w-full px-3 py-2.5 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Nomor HP / WhatsApp</label>
            <input data-testid="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} required minLength={8} className="mt-1.5 w-full px-3 py-2.5 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent" />
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <button type="submit" disabled={savingProfile} data-testid="profile-save" className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
              {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Simpan Perubahan
            </button>
          </div>
        </form>
      </div>

      {/* Password change */}
      <div className="bg-card border border-border rounded-2xl p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-9 w-9 rounded-full bg-accent/15 flex items-center justify-center">
            <KeyRound className="h-4 w-4 text-accent" />
          </div>
          <div>
            <h2 className="font-serif text-xl text-primary">Ganti Password</h2>
            <p className="text-xs text-muted-foreground">
              {hasPassword ? "Ubah password akun Anda secara berkala untuk keamanan." : "Fitur ini hanya untuk akun yang mendaftar via email/password."}
            </p>
          </div>
        </div>
        <form onSubmit={savePassword} className="space-y-4 max-w-md" data-testid="change-password-form">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Password Saat Ini</label>
            <div className="mt-1.5 relative">
              <input data-testid="cp-current" type={showPw ? "text" : "password"} required minLength={8} value={curPw} onChange={(e) => setCurPw(e.target.value)} className="w-full px-3 py-2.5 pr-10 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Password Baru</label>
            <input data-testid="cp-new" type="password" required minLength={8} value={newPw} onChange={(e) => setNewPw(e.target.value)} className="mt-1.5 w-full px-3 py-2.5 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent" placeholder="Minimal 8 karakter" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Konfirmasi Password Baru</label>
            <input data-testid="cp-confirm" type="password" required minLength={8} value={confPw} onChange={(e) => setConfPw(e.target.value)} className="mt-1.5 w-full px-3 py-2.5 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent" />
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={savingPw} data-testid="cp-save" className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
              {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              Ubah Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
